import { useCallback } from 'react';

import { ColumnConfig, TableRowData } from './types';

/**
 * Escapes a single CSV cell value per RFC 4180:
 * - Wraps in double quotes if the value contains commas, double quotes, or newlines
 * - Doubles any embedded double quotes
 */
function escapeCsvValue(value: unknown): string {
  if (value === null || value === undefined) return '';

  let str: string;
  if (Array.isArray(value)) {
    str = value.join('; ');
  } else {
    str = String(value);
  }

  if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
    str = `"${str.replace(/"/g, '""')}"`;
  }

  return str;
}

/**
 * Reusable hook that generates a downloadCSV function for a DataTable.
 *
 * @param filteredData - The currently filtered + sorted rows (raw values, not JSX)
 * @param visibleColumns - Visible columns in display order
 * @param filename - Desired filename for the downloaded file (should end in .csv)
 */
export function useExportTableCSV(
  filteredData: TableRowData[],
  visibleColumns: ColumnConfig[],
  filename: string,
): { downloadCSV: () => void } {
  const downloadCSV = useCallback(() => {
    const headerRow = visibleColumns.map((col) => escapeCsvValue(col.label)).join(',');

    const dataRows = filteredData.map((row) =>
      visibleColumns.map((col) => escapeCsvValue(row[col.key])).join(','),
    );

    const csv = [headerRow, ...dataRows].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.setAttribute('download', filename);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 200);
  }, [filteredData, visibleColumns, filename]);

  return { downloadCSV };
}
